import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/api';
import PublicPreviewLink from '../components/PublicPreviewLink';
import GenerationPreviewButton from '../components/GenerationPreviewButton';
import { formatGenerationTemplate, formatGenerationSiteType } from '../utils/adminHelpers';

interface Generation {
  id: number;
  generation_id: string;
  template_type: string;
  jurisdiction: string;
  site_type: string;
  language: string;
  status: string;
  created_at: string;
  archive_path?: string;
}

function getStatusText(status: string) {
  switch (status) {
    case 'completed':
      return 'Завершено';
    case 'queued':
      return 'В очереди';
    case 'pending':
      return 'В обработке';
    case 'failed':
      return 'Ошибка';
    case 'processing':
      return 'Обработка';
    default:
      return status;
  }
}

function getStatusPillClass(status: string) {
  if (status === 'completed') return 'is-ok';
  if (status === 'failed') return 'is-bad';
  return 'is-warn';
}

const HistoryV5: React.FC = () => {
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [downloading, setDownloading] = useState<number | null>(null);
  const [actionError, setActionError] = useState('');
  const [deleting, setDeleting] = useState<number | null>(null);
  const visibleGenerations = generations.filter(g => {
    const matchesStatus = statusFilter === 'all' || (statusFilter === 'processing' ? ['pending', 'processing', 'queued'].includes(g.status) : g.status === statusFilter);
    const query = search.trim().replace(/^#(?=\d+$)/, '').toLocaleLowerCase('ru-RU');
    return matchesStatus && `${g.id} ${g.generation_id} ${formatGenerationTemplate(g.template_type)} ${g.language}`.toLocaleLowerCase('ru-RU').includes(query);
  });

  const formatDateTime = useMemo(() => {
    return (value: string) =>
      new Date(value).toLocaleString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
  }, []);

  const fetchGenerations = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Необходимо войти в систему');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get('/api/users/generations');
      setGenerations(response.data || []);
    } catch (e) {
      console.error('Error fetching generations:', e);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGenerations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async (generationId: number) => {
    if (downloading !== null) return;
    setDownloading(generationId);
    setActionError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Необходимо войти в систему');
        return;
      }

      const response = await axios.get(`/api/users/generations/${generationId}/download`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `generation-${generationId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error downloading file:', e);
      setActionError('Не удалось скачать архив. Повторите скачивание.');
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (generationId: number) => {
    if (deleting !== null || !window.confirm(`Удалить сайт №${generationId} из истории? Его превью и скачивание станут недоступны.`)) return;
    setDeleting(generationId);
    setActionError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await axios.delete(`/api/users/generations/${generationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGenerations((prev) => prev.filter((g) => g.id !== generationId));
    } catch (e) {
      console.error('Error deleting generation:', e);
      setActionError('Не удалось удалить сайт. Запись осталась в истории.');
    } finally { setDeleting(null); }
  };

  if (loading) {
    return (
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-strong">Загрузка истории…</div>
          <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>Собираем список ваших генераций.</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-cardHeader">
            <div>
              <h1 className="u2-title" style={{ fontSize: 22 }}>Ошибка</h1>
              <p className="u2-subtitle">{error}</p>
            </div>
            <span className="u2-pill is-bad">ошибка</span>
          </div>
          <div className="u2-divider" />
          <div className="u2-toolbarGroup">
            <button className="u2-btn is-primary" type="button" onClick={fetchGenerations}>Попробовать снова</button>
            <Link className="u2-btn is-secondary" to="/dashboard">Обзор</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="u2-grid" style={{ gap: 18 }}>
      <section className="u2-card is-gradient">
      <div className="u2-cardInner">
        <div className="u2-cardHeader">
          <div>
            <h1 className="u2-title" style={{ fontSize: 22 }}>История генераций</h1>
            <p className="u2-subtitle">Готовые архивы, выполняющиеся задачи и неудачные попытки.</p>
          </div>
          <Link className="u2-btn is-primary" to="/generator-v2">Создать новую</Link>
        </div>

        <div className="u2-divider" />

        {actionError && <div className="error-message" role="alert">{actionError}</div>}
        <div className="u2-toolbar history-filters">
          <label className="u2-field">Поиск сайта<input type="search" className="u2-input" placeholder="Номер, шаблон или язык" value={search} onChange={e => setSearch(e.target.value)} /></label>
          <label className="u2-field">Статус<select className="u2-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}><option value="all">Все статусы</option><option value="completed">Готовые</option><option value="processing">В работе</option><option value="failed">С ошибкой</option></select></label>
          <button type="button" className="u2-btn is-secondary" onClick={fetchGenerations}>Обновить список</button>
          <span className="u2-muted" role="status">Показано {visibleGenerations.length} из {generations.length}</span>
        </div>
        {generations.length === 0 ? (
          <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
            <div className="u2-cardInner">
              <div className="u2-strong">Нет генераций</div>
              <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>Создайте первый сайт в генераторе.</div>
              <div className="u2-divider" />
              <Link className="u2-btn is-primary" to="/generator-v2">Открыть генератор</Link>
            </div>
          </div>
        ) : (
          <>
            {visibleGenerations.length === 0 && <div className="u2-selectionSummary">По заданным условиям сайтов нет. <button type="button" className="u2-btn is-secondary" onClick={() => { setSearch(''); setStatusFilter('all'); }}>Сбросить фильтры</button></div>}
            <div className="u2-tableWrap" aria-label="История генераций">
            <table className="u2-table u2-siteTable">
              <thead>
                <tr>
                  <th>Сайт</th>
                  <th>Шаблон</th>
                  <th>Юрисдикция</th>
                  <th>Формат</th>
                  <th>Язык</th>
                  <th>Статус</th>
                  <th>Дата</th>
                  <th style={{ textAlign: 'right' }}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {visibleGenerations.map((g) => (
                  <tr key={g.id}>
                    <td data-label="Номер" title={g.generation_id}>#{g.id}</td>
                    <td data-label="Шаблон">{formatGenerationTemplate(g.template_type)}</td>
                    <td data-label="Юрисдикция">{g.jurisdiction}</td>
                    <td data-label="Формат">{formatGenerationSiteType(g.site_type)}</td>
                    <td data-label="Язык">{g.language}</td>
                    <td data-label="Статус">
                      <span className={`u2-pill ${getStatusPillClass(g.status)}`}>{getStatusText(g.status)}</span>
                    </td>
                    <td data-label="Создан">{formatDateTime(g.created_at)}</td>
                    <td data-label="Действия" style={{ textAlign: 'right' }}>
                      <div className="u2-toolbarGroup" style={{ justifyContent: 'flex-end' }}>
                        {g.status === 'completed' && g.archive_path ? (
                          <><GenerationPreviewButton generationId={g.generation_id} />
                          <button className="u2-btn is-secondary" type="button" disabled={downloading !== null} onClick={() => handleDownload(g.id)}>
                            {downloading === g.id ? 'Скачиваем…' : 'Скачать ZIP'}
                          </button><PublicPreviewLink generationId={g.generation_id} /></>
                        ) : null}
                        <button className="u2-btn is-danger" type="button" disabled={deleting !== null || ['pending', 'processing', 'queued'].includes(g.status)} onClick={() => handleDelete(g.id)}>
                          {deleting === g.id ? 'Удаляем…' : 'Удалить'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>
    </section>
      <section className="u2-card" style={{ background: 'var(--u2-surface-3)', borderColor: 'var(--u2-accent)' }}>
        <div className="u2-cardInner">
          <div className="u2-row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div className="u2-strong">Пополнения и операции с балансом</div>
              <div className="u2-muted" style={{ fontSize: 12 }}>
                Доступны в общем кабинете портала.
              </div>
            </div>
            <a
              className="u2-btn is-primary"
              href={`/api/portal/balance/topup?section=ledger&source=whitepage-generator&returnUrl=${encodeURIComponent(window.location.href)}`}
            >
              Открыть бухгалтерию →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HistoryV5;
